import React from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  ISSUE_META,
  STATUS_META,
  clockTime,
  fullName,
  hours,
  issueDetail,
  longDate,
  num,
  shiftTime,
} from "./format";

export const EMPLOYEE_TABS = [
  { id: "all", label: "All" },
  { id: "present", label: "Present" },
  { id: "punched_in", label: "Punched In" },
  { id: "not_punched_in", label: "Not Punched In" },
  { id: "absent", label: "Absent" },
  { id: "leave", label: "Leave" },
  { id: "working", label: "Working" },
  { id: "weekoff", label: "Week Off" },
];

export const EXCEPTION_TABS = [
  { id: "all", label: "All exceptions", color: "#3a1fd9" },
  { id: "not_punched_in", label: "Not Punched In", color: "#8a8a9e" },
  { id: "missing_punch_out", label: "Missing Punch Out", color: "#7c3aed" },
  { id: "insufficient_hours", label: "Insufficient Hours", color: "#e0a106" },
  { id: "multiple_punches", label: "Multiple Punches", color: "#2563eb" },
  { id: "outside_geofence", label: "Outside Geofence", color: "#dc3545" },
  { id: "unusual_attendance", label: "Unusual Attendance", color: "#dc3545" },
];

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, tone: "grey" };
  return <span className={`a2-pill bg-${meta.tone}`}>{meta.label}</span>;
};

const IssuePill = ({ issue }) => {
  const meta = ISSUE_META[issue] || { label: issue, tone: "grey" };
  return <span className={`a2-pill bg-${meta.tone}`}>{meta.label}</span>;
};

const Pager = ({ page, pageSize, count, onPage }) => {
  const pages = Math.max(1, Math.ceil(count / pageSize));
  const from = count ? (page - 1) * pageSize + 1 : 0;
  return (
    <div className="a2-pager a2-noprint">
      <span>
        Showing {num(from)}–{num(Math.min(count, page * pageSize))} of {num(count)}
      </span>
      <div>
        <button className="a2-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
        <b style={{ color: "var(--a2-text)" }}>Page {num(page)} of {num(pages)}</b>
        <button className="a2-btn" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button>
      </div>
    </div>
  );
};

export const EmployeeTable = ({ data, loading, tab, onTab, search, onSearch, onPage, onOpen, multiDay, scopeLabel, innerRef }) => {
  const rows = data?.results || [];
  const counts = data?.tab_counts || {};
  return (
    <div ref={innerRef} className={`a2-card a2-flush a2-section ${loading ? "a2-loading" : ""}`}>
      <div className="a2-card-head">
        <div>
          <h3 className="a2-card-title">Employee Attendance</h3>
          <div className="a2-card-sub">
            {num(counts.all)} {multiDay ? "employee-days" : "employees"} · {scopeLabel}
          </div>
        </div>
        <div className="a2-search a2-noprint" style={{ width: 320, maxWidth: "100%" }}>
          <SearchIcon />
          <input className="a2-input" placeholder="Search ID, name, mobile" value={search} onChange={(e) => onSearch(e.target.value)} />
        </div>
      </div>
      <div className="a2-tabs a2-noprint" style={{ padding: "0 20px 14px" }}>
        {EMPLOYEE_TABS.map((t) => (
          <button key={t.id} className={`a2-tab ${tab === t.id ? "active" : ""}`} onClick={() => onTab(t.id)}>
            {t.label} <span>{num(counts[t.id] || 0)}</span>
          </button>
        ))}
      </div>
      <div className="a2-table-wrap">
        <table className="a2-table">
          <thead>
            <tr>
              {multiDay && <th>Date</th>}
              <th>Employee ID</th>
              <th>Employee Name</th>
              <th>Client</th>
              <th>Designation</th>
              <th>Shift</th>
              <th>Punch In</th>
              <th>Punch Out</th>
              <th>Status</th>
              <th className="num">Working Hours</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.user_uuid}-${r.date}`} className="clickable" onClick={() => onOpen(r)}>
                {multiDay && <td>{longDate(r.date)}</td>}
                <td className="a2-id">{r.employee_id || "—"}</td>
                <td>
                  <div className="a2-name">{fullName(r)}</div>
                  <div className="a2-small">{r.mobile || ""}</div>
                </td>
                <td>
                  <div>{r.client_name || "—"}</div>
                  <div className="a2-small">{r.location_name || ""}</div>
                </td>
                <td>{r.designation || "—"}</td>
                <td>
                  <div>{r.shift_name || "—"}</div>
                  {r.shift_from && (
                    <div className="a2-small">
                      {shiftTime(r.shift_from)} – {shiftTime(r.shift_to)}
                    </div>
                  )}
                </td>
                <td>{clockTime(r.punch_in)}</td>
                <td>{clockTime(r.punch_out)}</td>
                <td>
                  <StatusPill status={r.attendance_status} />
                </td>
                <td className="num">{hours(r.worked)}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={multiDay ? 10 : 9} className="a2-empty">No employees match the current filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pager page={data?.page || 1} pageSize={data?.page_size || 10} count={data?.count || 0} onPage={onPage} />
    </div>
  );
};

export const ExceptionTable = ({ data, loading, type, onType, onMore, onOpen, multiDay, scopeLabel }) => {
  const rows = data?.results || [];
  const counts = data?.type_counts || {};
  const remaining = Math.max(0, (data?.count || 0) - rows.length);
  return (
    <div className={`a2-card a2-flush a2-section ${loading ? "a2-loading" : ""}`}>
      <div className="a2-card-head">
        <div>
          <h3 className="a2-card-title">Attendance Exceptions</h3>
          <div className="a2-card-sub">
            {num(counts.all)} {multiDay ? "employee-days" : "employees"} with at least one exception · {scopeLabel}
          </div>
        </div>
      </div>
      <div className="a2-tabs a2-noprint" style={{ padding: "0 20px 14px" }}>
        {EXCEPTION_TABS.map((t) => (
          <button key={t.id} className={`a2-tab ${type === t.id ? "active" : ""}`} onClick={() => onType(t.id)}>
            <i className="dot" style={{ background: type === t.id ? "#fff" : t.color }} />
            {t.label} <span>{num(counts[t.id] || 0)}</span>
          </button>
        ))}
      </div>
      <div className="a2-table-wrap">
        <table className="a2-table">
          <thead>
            <tr>
              {multiDay && <th>Date</th>}
              <th>Employee</th>
              <th>Client</th>
              <th>Issue</th>
              <th>Punch In</th>
              <th>Punch Out</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const shown = type === "all" ? r.issues : [type];
              return (
                <tr key={`${r.user_uuid}-${r.date}`} className="clickable" onClick={() => onOpen(r)}>
                  {multiDay && <td>{longDate(r.date)}</td>}
                  <td>
                    <div className="a2-name">{fullName(r)}</div>
                    <div className="a2-small">
                      {r.employee_id || "—"} · {r.designation || "—"}
                    </div>
                  </td>
                  <td>
                    <div>{r.client_name || "—"}</div>
                    <div className="a2-small">{[r.zone_name, r.region_name].filter(Boolean).join(", ")}</div>
                  </td>
                  <td>
                    <IssuePill issue={shown[0]} />
                    {r.issues.length > 1 && type === "all" && (
                      <span className="a2-small" style={{ marginLeft: 6 }}>+{r.issues.length - 1} more</span>
                    )}
                  </td>
                  <td>{clockTime(r.punch_in)}</td>
                  <td>{clockTime(r.punch_out)}</td>
                  <td>{issueDetail({ ...r, issues: shown })}</td>
                </tr>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={multiDay ? 7 : 6} className="a2-empty">No exceptions for this view.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {remaining > 0 && onMore && (
        <div style={{ textAlign: "center", padding: 14 }} className="a2-noprint">
          <button className="a2-btn" style={{ color: "var(--a2-primary)" }} onClick={onMore}>
            Show more ({num(remaining)} remaining)
          </button>
        </div>
      )}
    </div>
  );
};
